import { Pause, Play } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/v2/alert-dialog";
import { Button } from "@/components/ui/v2/button";
import { useSetCampaignIntegrationPausedMutation } from "@/features/campaigns/jobboards/api/useSetCampaignIntegrationPausedMutation";
import useToast from "@/lib/hooks/useToast";
import { CampaignJobBoardType } from "@/types/response/CampaignJobBoardType";

type CampaignIntegrationPauseButtonProps = {
  campaignIntegration: CampaignJobBoardType;
  campaignId: string;
  clientId?: string;
};

/**
 * Pausing stops distribution and spend, and nothing on screen announces it beyond the status chip,
 * so it is confirmed. Resuming is not: it only puts the integration back to what its dates already
 * say, and the toast covers it.
 */
function CampaignIntegrationPauseButton({
  campaignIntegration,
  campaignId,
  clientId,
}: CampaignIntegrationPauseButtonProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const setPaused = useSetCampaignIntegrationPausedMutation({
    campaignId,
    clientId,
    clientIntegrationModuleId: campaignIntegration.clientIntegrationModuleId,
  });

  // An expired integration can be neither paused nor resumed — the API rejects it as
  // CampaignIntegration.Expired — so it gets no control rather than one that always fails.
  if (campaignIntegration.isExpired) {
    return null;
  }

  const willPause = !campaignIntegration.isPaused;

  const toggle = () => {
    setPaused.mutate(
      { isPaused: willPause },
      {
        onSuccess: () =>
          showToast(
            t(willPause ? "campaignIntegration.pause.paused" : "campaignIntegration.pause.resumed")
          ),
        onError: () =>
          showToast(
            t(
              willPause
                ? "campaignIntegration.pause.pauseFailed"
                : "campaignIntegration.pause.resumeFailed"
            ),
            "error"
          ),
      }
    );
  };

  const handleConfirm = () => {
    setIsConfirmOpen(false);
    toggle();
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        // Matches the sibling Edit button, so the two share a row on mobile.
        className="flex-1 h-9"
        loading={setPaused.isPending}
        onClick={() => (willPause ? setIsConfirmOpen(true) : toggle())}
      >
        {!setPaused.isPending &&
          (willPause ? (
            <Pause className="h-4 w-4 mr-1.5" />
          ) : (
            <Play className="h-4 w-4 mr-1.5" />
          ))}
        {t(
          willPause
            ? "campaignIntegration.pause.pauseAction"
            : "campaignIntegration.pause.resumeAction"
        )}
      </Button>

      <AlertDialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("campaignIntegration.pause.confirm.title")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("campaignIntegration.pause.confirm.description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm} disabled={setPaused.isPending}>
              {t("campaignIntegration.pause.confirm.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default CampaignIntegrationPauseButton;
